/** @forge-header
 * @id          boxingParser
 * @role        Boxing ticker parser | KXBOXING fight parsing
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,TennisMatchInfo | kalshiTicker/helpers:parseFallback,formatTennisPlayerCode,splitCompetitors
 * @downstream  kalshiTicker/parse
 * @exports     parseBoxing:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, TennisMatchInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, formatTennisPlayerCode, splitCompetitors } from '../helpers';

/**
 * Boxing: KXBOXING-26SEP19FMAYMPAC-FMAY
 * Fighter-vs-fighter, like UFC, but variable-length fighter codes — uses the
 * outcome code to anchor the split. Reuses the two-competitor `fight` field.
 */
export function parseBoxing(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) return parseFallback(raw);

  const mid = parts[1] ?? '';
  if (mid.length < 9) return parseFallback(raw); // 7 date + >=1+1 codes

  const date = parseDateFromTicker(mid.slice(0, 7));
  const outcome = parts[2] ?? null;
  const [c1, c2] = splitCompetitors(mid.slice(7), outcome);

  const fight: TennisMatchInfo = {
    player1: { playerCode: c1, displayName: formatTennisPlayerCode(c1) },
    player2: { playerCode: c2, displayName: formatTennisPlayerCode(c2) },
  };

  const shortLabel = outcome ? `Boxing: ${formatTennisPlayerCode(outcome)} Win` : 'Boxing';
  const fullLabel = `Boxing: ${fight.player1.displayName} vs ${fight.player2.displayName}${outcome ? ` · ${formatTennisPlayerCode(outcome)} Win` : ''}`;

  return {
    raw,
    category: 'boxing-fight',
    series: 'KXBOXING',
    date,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    fight,
    outcome,
    shortLabel,
    fullLabel,
  };
}
